/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "../api/axiosInstance"; // con token JWT automático
import { useNavigate, useSearchParams } from "react-router-dom";
import "../Reservations.css"; // Usa el mismo CSS que Reservations

const SelectCustomer = () => {
  const location = useLocation();
  const mode = location.state?.mode;
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id");

  const [maxAgeChild, setMaxAgeChild] = useState(null);
  const [maxAgeBaby, setMaxAgeBaby] = useState(null); 
  const [minAgeBookerFromSettings, setMinAgeBookerFromSettings] = useState(null);
  const [minAgeMainGuestFromSettings, setMinAgeMainGuestFromSettings] = useState(null);

  
  
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/customers`)
      .then((response) => {
        setCustomers(response.data.sort((a, b) => b.id - a.id));
      })
      .catch((error) => console.error("Error fetching customers:", error));
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: businessUnitId || 1 }
        });
        setMaxAgeChild(res.data.max_age_child);
        setMaxAgeBaby(res.data.max_age_baby); 
        setMinAgeBookerFromSettings(res.data.min_age_booker);
        setMinAgeMainGuestFromSettings(res.data.min_age_main_guest);
      } catch (err) {
        console.error("❌ Error fetching hotel settings:", err);
      }
    };
  
    fetchSettings();
  }, [businessUnitId]);
  
  const handleSelectCustomer = async (customerId) => {
  const customer = customers.find(c => c.id === customerId);
  if (!customer) return;

  // IDs de la URL (siguen siendo necesarios)
  const reservationId = searchParams.get("reservation_id");
  const guestType = searchParams.get("guest_type");
  const guestIndex = searchParams.get("guest_index");

  // NUEVO FLUJO: Solo para booker (usando mode por state)
  if (mode === "booker" && reservationId) {
    // Validación de edad mínima para booker
    const birthDate = customer.birth_date ? new Date(customer.birth_date) : null;
    const minAgeBooker = minAgeBookerFromSettings;

    if (birthDate && minAgeBooker !== null) {
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < minAgeBooker) {
        alert(`❌ This booker is too young. Must be at least ${minAgeBooker} years old. Current age: ${age}`);
        return;
      }
    }

    // PATCH para asignar el booker al grupo
    try {
      const res = await axios.get(`/api/reservations/${reservationId}`);
      const groupId = res.data.reservation_group_id;
      if (!groupId) {
        alert("❌ No reservation_group_id found. Cannot assign booker.");
        return;
      }
      await axios.patch(`/api/reservation-groups/${groupId}`, { booker_id: customerId });
      console.log("✅ Booker assigned successfully.");
      navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`);
    } catch (err) {
      console.error("❌ Error assigning booker:", err);
      alert("Error assigning booker.");
    }
    return;
  }

      
    if (mode === "accompanying" && reservationId) {
  // LEE el slot de forma robusta:
  let guestIndex = location.state?.guestIndex;
  console.log("DEBUG: location.state =", location.state);
  console.log("DEBUG: guestIndex antes de fallback:", guestIndex, typeof guestIndex);

  if (guestIndex === undefined || guestIndex === null) {
    // Fallback ultra robusto (ideal: eliminar esto en el futuro)
    const guestIndexFromURL = parseInt(searchParams.get("guest_index"));
    if (!isNaN(guestIndexFromURL)) guestIndex = guestIndexFromURL;
  }
  if (typeof guestIndex === "string") guestIndex = parseInt(guestIndex);
  console.log("DEBUG: guestIndex FINAL:", guestIndex, typeof guestIndex);

  if (guestIndex === undefined || guestIndex === null || isNaN(guestIndex)) {
    alert("Error: guestIndex is missing. No slot to assign!");
    return;
  }

  // Validación de edad mínima para acompañante
  if (customer.birth_date) {
    const birthDate = new Date(customer.birth_date);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (maxAgeBaby !== null && age < maxAgeBaby) {
      alert(`❌ This customer is too young to be assigned as accompanying guest.\nCurrent age: ${age} years`);
      return;
    }
  }

  try {
    console.log("Asignando accompanying guest:", {
      reservation_id: reservationId,
      customer_id: customerId,
      guest_index: guestIndex,
    });

    await axios.post("/api/reservation-guests", {
      reservation_id: reservationId,
      customer_id: customerId,
      is_main_guest: false,
      is_child: false,
      guest_index: guestIndex
    });

    // Navega de vuelta a la reserva solo con business_unit_id y reservation_id
    navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`);
  } catch (error) {
    console.error("❌ Error assigning accompanying guest:", error);
    alert("Error assigning accompanying guest.");
  }
  return;
}


 
    if (mode === "child" && reservationId) {
      let guestIndex = location.state?.guestIndex;
      if (typeof guestIndex === "string") guestIndex = parseInt(guestIndex);

      if (guestIndex === undefined || guestIndex === null || isNaN(guestIndex)) {
        alert("Error: guestIndex is missing. No slot to assign!");
        return;
      }

      // 🔒 Validación de edad para CHILD
      if (customer.birth_date) {
        const birthDate = new Date(customer.birth_date);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        if (maxAgeBaby !== null && age <= maxAgeBaby) {
          alert(`❌ This child is too young (<= ${maxAgeBaby} years). Current age: ${age}`);
          return;
        }

        if (maxAgeChild !== null && age > maxAgeChild) {
          alert(`❌ This child is too old (> ${maxAgeChild} years). Current age: ${age}`);
          return;
        }
      }

      try {
        console.log("Asignando child:", {
          reservation_id: reservationId,
          customer_id: customerId,
          guest_index: guestIndex,
        });

        await axios.post("/api/reservation-guests", {
          reservation_id: reservationId,
          customer_id: customerId,
          is_main_guest: false,
          is_child: true,
          guest_index: guestIndex
        });

        // Navega de vuelta solo con los parámetros mínimos
        navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`);
      } catch (error) {
        console.error("❌ Error assigning child:", error);
        alert("Error assigning child.");
      }
      return;
    }

    
    
 
    
  
    // 🚨 Reemplaza todo tu bloque antiguo por esto:
    if (mode === "main_guest" && reservationId) {
      // Validación de edad mínima para main guest
      if (customer.birth_date) {
        const birthDate = new Date(customer.birth_date);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        if (minAgeMainGuestFromSettings !== null && age < minAgeMainGuestFromSettings) {
          alert(`❌ This customer is too young to be assigned as main guest.\nMust be at least ${minAgeMainGuestFromSettings} years old. Current age: ${age}`);
          return;
        }
      }

      try {
        // Inserta el main guest en reservation_guests
        await axios.post("/api/reservation-guests", {
          reservation_id: reservationId,
          customer_id: customerId,
          is_main_guest: true,
          is_child: false,
        });

        // Navega de vuelta a la reserva (sin parámetros extra)
        navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`);
      } catch (error) {
        console.error("❌ Error assigning main guest:", error);
        alert("Error assigning main guest.");
      }
      return;
    }    
    
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [column]: value }));
  };

  const filteredCustomers = customers.filter((customer) => {
    return Object.entries(filters).every(([column, filterValue]) => {
      if (!filterValue) return true;
      if (column === "Name") {
        const fullName = `${customer.first_name} ${customer.middle_name || ""} ${customer.last_name} ${customer.second_last_name || ""}`.trim().toLowerCase();
        return fullName.includes(filterValue.toLowerCase());
      }

      if (column === "Birth Date (Age)") {
        const birth = customer.birth_date ? new Date(customer.birth_date).toLocaleDateString("en-GB") : "";
        const today = new Date();
        const birthDate = new Date(customer.birth_date);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      
        const full = `${birth} (${age})`.toLowerCase();
        return full.includes(filterValue.toLowerCase());
      }
      
      return customer[column.toLowerCase().replace(/\s+/g, "_")]
        ?.toString()
        .toLowerCase()
        .includes(filterValue.toLowerCase());
    });
  });

  return (
    <div className="reservations-container">
      <button
        className="new-reservation-btn"
        onClick={() => {
          const reservationId = searchParams.get("reservation_id");
          const businessUnitId = searchParams.get("business_unit_id") || "1";
          if (reservationId) {
            navigate(`/new-reservation/${reservationId}?business_unit_id=${businessUnitId}`);
          } else {
            navigate(`/new-reservation?business_unit_id=${businessUnitId}`);
          }
        }}
      >
        ← Back to New Reservation
      </button>





      <button
        className="btn text-white fw-bold border-0 ms-3"
        style={{
          background: "linear-gradient(to right, #9b59b6, #5B2C6F)",
          borderRadius: "5px",
          padding: "10px 20px",
        }}
      >
        Export to Excel
      </button>

      <div className="table-container">
        <table>
          <thead>
            <tr className="text-light" style={{ backgroundColor: "#b9881f" }}>
              {[
                "Select", "ID", "Name", "Email", "Phone", "Country", "City", "Address", "Postal Code", "Document", "Birth Date (Age)"
              ].map((header, index) => (
                <th key={index}>
                  {header}
                  {header !== "Select" && (
                    <input
                      type="text"
                      className="form-control form-control-sm mt-1"
                      style={{ width: "100%", fontSize: "12px" }}
                      placeholder="Filter..."
                      onChange={(e) => handleFilterChange(header, e.target.value)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td>
                    <button
                      onClick={() => handleSelectCustomer(customer.id)}
                      className="text-warning fw-bold border-0 bg-transparent"
                      style={{ cursor: "pointer" }}
                    >
                      Select
                    </button>
                  </td>
                  <td>{customer.id}</td>
                  <td>{`${customer.first_name} ${customer.middle_name || ""} ${customer.last_name} ${customer.second_last_name || ""}`.trim()}</td>
                  <td>{customer.email || "-"}</td>
                  <td>{customer.phone || "-"}</td>
                  <td>{customer.country}</td>
                  <td>{customer.city || "-"}</td>
                  <td>{customer.address || "-"}</td>
                  <td>{customer.postal_code || "-"}</td>
                  <td>{`${customer.document_type} - ${customer.document_number}`}</td>
                  <td>
                    {customer.birth_date ? (
                      <>
                        {new Date(customer.birth_date).toLocaleDateString("en-GB")}{" "}
                        (<strong>
                          {(() => {
                            const birthDate = new Date(customer.birth_date);
                            const today = new Date();
                            let age = today.getFullYear() - birthDate.getFullYear();
                            const m = today.getMonth() - birthDate.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                              age--;
                            }
                            return age;
                          })()}
                        </strong> yrs)
                      </>
                    ) : (
                      "-"
                    )}
                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" style={{ textAlign: "center", padding: "10px" }}>
                  No customers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SelectCustomer;

